import React, { useLayoutEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import Link from '../components/SafeLink';
import { useAuth } from '../store/Auth';
import { T, Icon, useText } from '../components/Text';
import { Connection } from '../components/Layout';
import { useResource, usePage } from '../hooks/useResource';
import Api from '../services/api';
import Metrics from './Metrics';

/**
 * Ana Sayfa & Dashboard (/admin.html, /customer.html)
 * Giriş yapan kullanıcının rolüne göre Yönetici Paneli veya Müşteri Paneli sunar.
 */
export default function Home() {
  const { user } = useAuth();
  const { t, locale } = useText();
  const route = useLocation();

  const manager = Api.isManager(user);
  const isMetrics = route.hash === '#metrics';
  const isAtasay = route.hash === '#atasay';

  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const today=Api.today();
  useLayoutEffect(()=>{if(manager && !isMetrics) return Api.subscribeOrdersDate(today);},[manager,isMetrics,today,user?.uid,user?.branchId]);
  const orderResource = useResource(manager && isMetrics ? null : '/api/orders?date='+today);
  const customersResource = useResource(
    manager && !isMetrics ? '/api/customer-options' : null,
    ['customers-changed', 'orders-changed']
  );

  usePage(
    t(manager ? 'Yönetim Paneli' : 'Müşteri Paneli'),
    `admin-page ${!manager ? 'customer-page' : ''}`
  );

  // Bugünkü siparişler ve hesaplamalar
  const todayOrders = (orderResource.data?.orders || []).filter((o) => o.date === Api.today());
  const completedCount = todayOrders.filter((o) => o.completed).length;
  const remainingCount = todayOrders.length - completedCount;
  const rate = todayOrders.length ? (completedCount / todayOrders.length) * 100 : 0;

  // Müşterilerin bugünkü sipariş durumu
  const customerRows = (customersResource.data?.customers || [])
    .map((c) => ({
      ...c,
      total: todayOrders.filter((o) => o.customerId === c.id).length,
      completed: todayOrders.filter((o) => o.customerId === c.id && o.completed).length,
    }))
    .filter((c) => c.total > 0);

  // Arama ve durum filtresi
  const filteredCustomers = customerRows.filter((c) => {
    const matchesQuery = c.name.toLocaleLowerCase('tr-TR').includes(query.toLocaleLowerCase('tr-TR'));
    const matchesStatus =
      statusFilter === 'all' ||
      (statusFilter === 'complete' ? c.completed === c.total : c.completed < c.total);
    return matchesQuery && matchesStatus;
  });

  const num = (n) => new Intl.NumberFormat(locale).format(n);
  const error = orderResource.error || customersResource.error;

  // Eğer doğrudan metrikler sekmesi istendiyse
  if (manager && isMetrics) {
    return <Metrics />;
  }

  // -------------------------------------------------------------
  // MÜŞTERİ PANELİ GÖRÜNÜMÜ
  // -------------------------------------------------------------
  if (!manager) {
    return (
      <main id="main" className="dashboard customer-dashboard">
        <section className="welcome" aria-labelledby="welcome-title">
          <div>
            <div className="eyebrow">
              <T>MÜŞTERİ PANELİ</T>
            </div>
            <h1 id="welcome-title">
              <T>Hoş geldin, </T>
              <em>{user.displayName}</em>
            </h1>
            <p>
              <T>Siparişlerinin durumu, bir bakışta elinin altında.</T>
            </p>
          </div>
        </section>

        <div className="preview-note">
          <span className="connection-status">
            <Connection />
          </span>
        </div>

        {error && (
          <p className="form-error" role="alert">
            <T>{error}</T>
          </p>
        )}

        <section className="metrics-grid customer-metrics" aria-label={t('Siparişlerinin özeti')}>
          <article className="metric-card">
            <div className="metric-heading">
              <span>
                <T>BUGÜNKÜ SİPARİŞLERİN</T>
              </span>
              <Icon name="sheets" />
            </div>
            <div className="metric-number">{num(todayOrders.length)}</div>
            <p>
              <T>Bugün tarihli siparişlerin</T>
            </p>
            <div className="metric-accent" aria-hidden="true" />
          </article>

          <article className="metric-card completed-card">
            <div className="metric-heading">
              <span>
                <T>HAZIR SİPARİŞLERİN</T>
              </span>
              <Icon name="sheets" />
            </div>
            <div className="metric-number">{num(completedCount)}</div>
            <p>
              <span className="status-dot" aria-hidden="true" />
              <T> Bugünkü siparişlerinden teslim almaya hazır olanlar</T>
            </p>
            <div className="metric-accent" aria-hidden="true" />
          </article>
        </section>

        <Link className="orders-shortcut" to="/orders">
          <span className="shortcut-icon" aria-hidden="true">
            <Icon name="list-view" />
          </span>
          <div className="shortcut-copy">
            <span className="eyebrow">
              <T>SİPARİŞ TAKİBİ</T>
            </span>
            <h2>
              <T>Siparişlerini Gör</T>
            </h2>
            <span className="shortcut-description">
              <T>Bugünkü siparişlerinin detaylarını ve durumlarını incele.</T>
            </span>
          </div>
          <span className="shortcut-arrow" aria-hidden="true">
            ↗
          </span>
        </Link>
      </main>
    );
  }

  // -------------------------------------------------------------
  // YÖNETİCİ PANELİ GÖRÜNÜMÜ
  // -------------------------------------------------------------
  return (
    <main id="main" className="dashboard">
      {isAtasay && (
        <section className="atasay-coming-soon" aria-label={t('Atasay şubesi')}>
          <h1>
            <T>Çok yakında</T>
          </h1>
        </section>
      )}

      <div id="dashboard-home">
        {/* Karşılama Başlığı */}
        <section className="welcome" aria-labelledby="welcome-title">
          <div>
            <div className="eyebrow">
              <T>YÖNETİM PANELİ</T>
            </div>
            <h1 id="welcome-title">
              <T>Hoş geldiniz, </T>
              <em>{user.displayName}</em>
            </h1>
            <p>
              <T>Bugünkü siparişlerinize ve müşterilerinizin üretim durumuna bir bakışta göz atın.</T>
            </p>
          </div>
          <Link className="button gold" to="/order-create">
            <T>Sipariş Oluştur </T>
            <span aria-hidden="true">↗</span>
          </Link>
        </section>

        <div className="preview-note">
          <span className="connection-status">
            <Connection />
          </span>
        </div>

        {error && (
          <p className="form-error" role="alert">
            <T>{error}</T>
          </p>
        )}

        {/* Günlük Metrik Kartları */}
        <section className="metrics-grid" id="today-summary" aria-label={t('Bugünkü sipariş özeti')}>
          {/* Toplam Sipariş */}
          <article className="metric-card">
            <div className="metric-heading">
              <span>
                <T>BUGÜNKÜ SİPARİŞLER</T>
              </span>
              <Icon name="sheets" />
            </div>
            <div className="metric-number">{num(todayOrders.length)}</div>
            <p>
              <span>{num(customerRows.length)}</span>
              <T> müşterinin bugün siparişi var</T>
            </p>
            <div className="metric-accent" aria-hidden="true" />
          </article>

          {/* Tamamlananlar */}
          <article className="metric-card completed-card">
            <div className="metric-heading">
              <span>
                <T>BUGÜNÜN TAMAMLANANLARI</T>
              </span>
              <Icon name="sheets" />
            </div>
            <div className="metric-number">{num(completedCount)}</div>
            <p>
              <span className="status-dot" aria-hidden="true" />
              <T> Bugünkü siparişlerden hazır olanlar</T>
            </p>
            <div className="metric-accent" aria-hidden="true" />
          </article>

          {/* İlerleme */}
          <article className="metric-card progress-card">
            <div>
              <div className="metric-heading">
                <span>
                  <T>BUGÜNKÜ İLERLEME</T>
                </span>
              </div>
              <div className="metric-number progress-number">%{Math.round(rate)}</div>
              <p>
                <span>{num(remainingCount)}</span>
                <T> sipariş tamamlanmadı</T>
              </p>
            </div>
            <div
              className="summary-ring"
              aria-hidden="true"
              style={{ '--progress': `${rate}%` }}
            >
              <span>↗</span>
            </div>
          </article>
        </section>

        {/* Müşteriler Paneli */}
        <section className="customer-panel" id="customers" aria-labelledby="customers-title">
          <div className="customer-panel-heading">
            <div>
              <div className="eyebrow">
                <T>ÜRETİMİN NABZI</T>
              </div>
              <h2 id="customers-title">
                <T>Müşteriler</T>
              </h2>
              <p>
                <T>Bugün siparişi olan müşterilerin üretim durumu.</T>
              </p>
            </div>

            {/* Arama Kutusu */}
            <label className="customer-search">
              <Icon name="search" />
              <input
                id="customer-search"
                type="search"
                placeholder={t('Müşteri ara…')}
                aria-label={t('Müşteri ara')}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
            </label>
          </div>

          {/* Filtre Butonları */}
          <div className="customer-filters" role="group" aria-label={t('Duruma göre filtrele')}>
            <button
              type="button"
              className={`filter-button ${statusFilter === 'all' ? 'selected' : ''}`}
              aria-pressed={statusFilter === 'all'}
              onClick={() => setStatusFilter('all')}
            >
              <T>Tümü</T>
            </button>
            <button
              type="button"
              className={`filter-button ${statusFilter === 'ongoing' ? 'selected' : ''}`}
              aria-pressed={statusFilter === 'ongoing'}
              onClick={() => setStatusFilter('ongoing')}
            >
              <T>Devam Edenler</T>
            </button>
            <button
              type="button"
              className={`filter-button ${statusFilter === 'complete' ? 'selected' : ''}`}
              aria-pressed={statusFilter === 'complete'}
              onClick={() => setStatusFilter('complete')}
            >
              <T>Tamamlananlar</T>
            </button>
          </div>

          {/* Müşteri Listesi */}
          <div className="customer-list-wrap">
            <span className="result-count">
              <T>{`${num(filteredCustomers.length)} müşteri`}</T>
            </span>

            {filteredCustomers.length === 0 ? (
              <p className="empty-state">
                <T>
                  {customerRows.length
                    ? 'Bu aramaya uygun müşteri bulunamadı.'
                    : 'Bugün siparişi olan müşteri bulunmuyor.'}
                </T>
              </p>
            ) : (
              <ul className="customer-list">
                {filteredCustomers.map((customer) => {
                  const customerPercent = (customer.completed / customer.total) * 100;
                  const isAllComplete = customer.total === customer.completed;
                  const initials = customer.name
                    .split(' ')
                    .map((part) => part[0])
                    .join('')
                    .slice(0, 2);

                  return (
                    <li
                      key={customer.id}
                      className={`customer-row ${isAllComplete ? 'complete' : ''}`}
                    >
                      <div className="customer-identity">
                        <span className="customer-avatar">{initials}</span>
                        <div>
                          <h3 className="customer-name">{customer.name}</h3>
                          <span className="customer-caption">
                            <T>Bugünkü sipariş özeti</T>
                          </span>
                        </div>
                      </div>

                      <div className="customer-count">
                        <strong className="row-total">{num(customer.total)}</strong>
                        <span> / </span>
                        <span className="row-completed">{num(customer.completed)}</span>
                      </div>

                      <div className="customer-progress">
                        <div className="progress-caption">
                          <span className="progress-text">
                            <T>{`%${Math.round(customerPercent)} tamamlandı`}</T>
                          </span>
                          <span className="remaining-text">
                            <T>
                              {isAllComplete
                                ? 'Tümü tamamlandı'
                                : `${num(customer.total - customer.completed)} kalan`}
                            </T>
                          </span>
                        </div>
                        <div
                          className="progress-track"
                          role="progressbar"
                          aria-valuemin={0}
                          aria-valuemax={customer.total}
                          aria-valuenow={customer.completed}
                          style={{ '--progress': `${customerPercent}%` }}
                        >
                          <span className="progress-fill" />
                        </div>
                      </div>

                      <span className="customer-status">
                        <span aria-hidden="true" />
                        <span className="status-text">
                          <T>{isAllComplete ? 'Tamamlandı' : 'Devam ediyor'}</T>
                        </span>
                      </span>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </section>
      </div>
    </main>
  );
}
